// /en/apostille/$country/$doctype — EN mirror of /apostille/$country/$doctype (P31-E)
// 51 countries × 15 document types ≈ 765 EN long-tail landing pages
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { APOSTILLE_COUNTRIES, getApostilleCountry } from "@/data/apostille-countries";
import { TRANSLATION_DOCTYPES, getTranslationDocType } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { dynamicOgImage, hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Stamp, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, ArrowRight, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/apostille/$country/$doctype")({
  loader: ({ params }) => {
    const c = getApostilleCountry(params.country);
    const d = getTranslationDocType(params.doctype);
    if (!c || !d) throw notFound();
    return { country: params.country, doctype: params.doctype };
  },
  head: ({ params }) => {
    const c = getApostilleCountry(params.country);
    const d = getTranslationDocType(params.doctype);
    if (!c || !d) return { meta: [{ title: ctaTitle("Not found", "/en/apostille/$country/$doctype", "en")}] };
    const enPath = `/en/apostille/${c.slug}/${d.slug}`;
    const thPath = `/apostille/${c.slug}/${d.slug}`;
    const url = `${SITE}${enPath}`;
    const label = c.isHague ? "Apostille" : "Consular Legalization";
    const price = c.priceFromTh + d.surcharge;
    const ratingKey = `en-apostille/${c.slug}/${d.slug}`;
    const title = `${label} Thai ${d.nameEn} for ${c.nameEn} ${c.flag} from THB ${price.toLocaleString()} | NYC`;
    const desc = `${label} of Thai ${d.nameEn} for use in ${c.nameEn}. Sworn translation + MoJ + MFA${c.isHague ? " Apostille" : ` + ${c.nameEn} embassy`}. From THB ${price.toLocaleString()}, done in ${c.turnaround}.`;
    const og = dynamicOgImage({
      title: `${label} ${d.nameEn} → ${c.nameEn}`,
      subtitle: `From THB ${price.toLocaleString()} · ${c.turnaround}`,
      badge: `${d.nameEn.toUpperCase()} · ${c.nameEn.toUpperCase()}`,
    });
    return {
      meta: [
        { title: ctaTitle(title, "/en/apostille/$country/$doctype", "en") }, { name: "description", content: ctaDesc(desc, "/en/apostille/$country/$doctype", "en")},
        { name: "keywords", content: `${label} ${d.nameEn}, Thai ${d.nameEn} ${c.nameEn}, legalize ${d.nameEn} for ${c.nameEn}, apostille Thailand ${c.nameEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/apostille/$country/$doctype", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/apostille/$country/$doctype", "en")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: og },
        { property: "og:image:width", content: "1200" }, { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" }, { name: "twitter:image", content: og },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${label} Thai ${d.nameEn} for ${c.nameEn}`,
              serviceType: `${label} for ${d.nameEn} (Thailand → ${c.nameEn})`,
              inLanguage: "en",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(ratingKey),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `How to ${label.toLowerCase()} a Thai ${d.nameEn} for ${c.nameEn}`,
              inLanguage: "en", totalTime: "P7D",
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: price },
              step: [
                { "@type": "HowToStep", position: 1, name: "Submit original document", text: `Send the original or certified copy of your Thai ${d.nameEn} via LINE @nycli or email — quote within 30 minutes.` },
                { "@type": "HowToStep", position: 2, name: `Certified translation of ${d.nameEn}`, text: "MoJ-registered sworn translator translates the document with notarial certification." },
                { "@type": "HowToStep", position: 3, name: "MFA Thailand authentication", text: c.isHague ? "Filed at the Department of Consular Affairs — Apostille stamp under the 1961 Hague Convention." : "Filed at the Department of Consular Affairs to authenticate the translation." },
                ...(c.isHague ? [] : [{ "@type": "HowToStep", position: 4, name: `${c.nameEn} embassy legalization`, text: `Submitted to the ${c.nameEn} Embassy in Bangkok for final consular stamp.` }]),
                { "@type": "HowToStep", position: c.isHague ? 4 : 5, name: "Delivery", text: "EMS / Grab / pickup at our Silom office." },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Apostille / Legalization", item: `${SITE}/en/apostille` },
                { "@type": "ListItem", position: 3, name: `${label} → ${c.nameEn}`, item: `${SITE}/en/apostille/${c.slug}` },
                { "@type": "ListItem", position: 4, name: d.nameEn, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnApostilleComboPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/apostille" className="mt-4 inline-block text-gold underline">Back to Apostille hub</Link>
          <QuotableStatsStrip cluster="apostille" locale="en" variant="light" />
    </main>
  ),
});

function EnApostilleComboPage() {
  const { country, doctype } = Route.useLoaderData() as { country: string; doctype: string };
  const c = getApostilleCountry(country)!;
  const d = getTranslationDocType(doctype)!;
  const label = c.isHague ? "Apostille" : "Consular Legalization";
  const price = c.priceFromTh + d.surcharge;

  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== d.slug).slice(0, 8);
  const otherCountries = APOSTILLE_COUNTRIES.filter((x) => x.slug !== c.slug && x.region === c.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/85">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/40">/</span>
            <Link to="/en/apostille" className="hover:text-gold">Apostille</Link>
            <span className="px-1.5 text-white/40">/</span>
            <Link to="/en/apostille/$country" params={{ country: c.slug }} className="hover:text-gold">{c.nameEn}</Link>
            <span className="px-1.5 text-white/40">/</span>
            <span className="text-white">{d.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {label} · {d.nameEn} · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            {label} Thai {d.nameEn} for {c.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Complete {label.toLowerCase()} of your Thai {d.nameEn} for official use at {c.authority} ({c.nameEn}).
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Method", v: label },
              { k: "From", v: `THB ${price.toLocaleString()}` },
              { k: "Turnaround", v: c.turnaround },
              { k: "Destination", v: c.authority },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/15 bg-white/[0.06] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, est. 2010) handles {label} of Thai {d.nameEn} for {c.nameEn} end-to-end: sworn translation by MoJ-registered translators + Notary + MFA Department of Consular Affairs{c.isHague ? " (Apostille stamp)" : ` + ${c.nameEn} Embassy legalization`}. From THB {price.toLocaleString()}, completed in {c.turnaround}. Rated 4.9/5 across 2,147 reviews.
        </blockquote>

        <section className="mt-12 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">About {d.nameEn} for {c.nameEn}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
              A Thai {d.nameEn} used in {c.nameEn} ({c.authority}) must go through {d.certifications.join(" → ")} so the receiving authority can verify its legal authenticity.
            </p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {d.destinationUses.map((u) => (
                <li key={u} className="flex items-start gap-2 text-sm text-slate-700">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
                </li>
              ))}
            </ul>
          </div>
          <aside className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Price</div>
            <div className="mt-1 font-display text-3xl font-extrabold text-navy-deep">THB {price.toLocaleString()}</div>
            <div className="text-[12px] text-slate-500">First page · additional pages billed as used</div>
            <ul className="mt-4 space-y-2 text-[13px] text-slate-700">
              <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Includes sworn translation</li>
              <li className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 2 business days +THB 2,500</li>
              <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> MFA / embassy fees billed at cost</li>
            </ul>
            <div className="mt-5"><ContactCTA /></div>
          </aside>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{label} workflow for {d.nameEn}</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: 1, t: `Send ${d.nameEn}`, d: "LINE / Email" },
              { n: 2, t: "Sworn translation", d: "MoJ / Notary" },
              { n: 3, t: c.isHague ? "MFA Apostille" : "MFA + Embassy", d: c.isHague ? "Consular Affairs" : `${c.nameEn} Embassy` },
              { n: 4, t: "Delivery", d: "EMS / Grab" },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other documents commonly {label.toLowerCase()}d for {c.nameEn}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((o) => (
              <Link key={o.slug} to="/en/apostille/$country/$doctype" params={{ country: c.slug, doctype: o.slug }}
                className="group flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="flex-1">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameEn}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{o.nameTh}</div>
                </div>
                <ArrowRight className="mt-1 h-3.5 w-3.5 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {otherCountries.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{label} {d.nameEn} to nearby countries</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherCountries.map((o) => (
                <Link key={o.slug} to="/en/apostille/$country/$doctype" params={{ country: o.slug, doctype: d.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameEn}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.isHague ? "Apostille" : "Legalization"} · from THB {(o.priceFromTh + d.surcharge).toLocaleString()}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10 text-sm text-slate-600">
          เวอร์ชันภาษาไทย: <Link to="/apostille/$country/$doctype" params={{ country, doctype }} className="font-semibold text-amber-700 underline">/apostille/{country}/{doctype}</Link>
        </section>
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="apostille" locale="en" variant="light" />
    </main>
  );
}
